import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  fillingManagementDetails,
  fillingManagementListUsers,
  sendReminder,
  updateApprovalStatus,
} from "./callbacks";
import { IBucketPayloadType } from "../../../types/api/filling-management";

export const useUpdateApprovalStatus = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IBucketPayloadType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IBucketPayloadType>,
    unknown,
    { bucketId: string; status: string }
  > = {},
) => {
  return useMutation<
    TResponse<IBucketPayloadType>,
    unknown,
    { bucketId: string; status: string }
  >({
    mutationFn: async (data) => {
      const response = await updateApprovalStatus(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      onSuccess?.(res);
      options.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options.onError?.(error, ...rest);
    },

    ...options,
  });
};

export const useFIllingManagementList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IBucketPayloadType[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IBucketPayloadType[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<IBucketPayloadType[]>, Error>(
    {
      queryKey: [
        API_ENDPOINTS.FILLING_MANAGEMENT_LIST,
        { ...queryParams, total: null },
      ],
      queryFn: async () => {
        const res = await fillingManagementListUsers(queryParams);
        return res.data;
      },
      select: (res) => ({
        ...res,
        data: res.data.map((x) => ({ key: x._id, ...x })),
        qData: { ...queryParams, total: res.extra?.total },
      }),
      staleTime: 300000,
      initialDataUpdatedAt: 0,
      refetchOnWindowFocus: true,

      ...options,
    },
  );

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...params }));
    },
  };
};

export const useSendReminder = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<null>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<null>,
    unknown,
    { userId: string }
  > = {},
) => {
  return useMutation<TResponse<null>, unknown, { userId: string }>({
    mutationFn: async (data) => {
      const response = await sendReminder(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      onSuccess?.(res);
      options.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options.onError?.(error, ...rest);
    },

    ...options,
  });
};

export const useBucketDataDetail = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IBucketPayloadType>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IBucketPayloadType>, Error> => {
  return useQuery<TResponseWithQData<IBucketPayloadType>, Error>({
    queryKey: [API_ENDPOINTS.HOMEPAGE_DETAIL, id],
    queryFn: async () => {
      const res = await fillingManagementDetails(id);
      return res.data;
    },
    enabled: Boolean(id),
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });
};
